import { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { useAuth } from "@/client/lib/auth";
import { levelRank } from "@/shared/permissions";
import {
  Table2Icon,
  TheaterIcon,
  SettingsIcon,
  DatabaseIcon,
  ActivityIcon,
  ShieldCheckIcon,
  VaultIcon,
  LogOutIcon,
  UserIcon,
  ChevronRightIcon,
  LayoutDashboardIcon,
} from "lucide-react";
import {
  CONSOLE_DATASETS,
  CONSOLE_BASE,
  datasetHref,
  currentDatasetSlug,
} from "@/client/lib/console-datasets";

type NavItem = {
  label: string;
  href: string;
  icon: typeof Table2Icon;
  match: string;
  resource?: string;
  adminOnly?: boolean;
  /** Renders as an expandable group whose children are sub-items. */
  kind?: "dataconsole" | "instellingen";
};

type NavGroup = {
  label?: string;
  items: NavItem[];
};

// Grouped admin nav, each section under a heading with a divider between them. "Beheer" = the core
// work, "Monitoring" = the logs/back-ups, "Systeem" = settings + the personal vault. Items are
// filtered by capability (employees only see what they can read); a section whose items are all
// hidden disappears with its heading.
const INSTELLINGEN_ITEMS: { label: string; slug: string; adminOnly?: boolean }[] = [
  { label: "Team", slug: "team", adminOnly: true },
  { label: "Configuratie", slug: "configuratie" },
];

const NAV_GROUPS: NavGroup[] = [
  {
    items: [
      { label: "Overzicht", href: "/admin/overzicht", icon: LayoutDashboardIcon, match: "/admin/overzicht", resource: "overzicht" },
    ],
  },
  {
    label: "Beheer",
    items: [
      { label: "Dataconsole", href: CONSOLE_BASE, icon: Table2Icon, match: "/admin/scholen", kind: "dataconsole" },
      { label: "Aanbod", href: "/admin/aanbod", icon: TheaterIcon, match: "/admin/aanbod", resource: "aanbod" },
    ],
  },
  {
    label: "Monitoring",
    items: [
      { label: "Back-ups", href: "/admin/backups", icon: DatabaseIcon, match: "/admin/backups", resource: "backups" },
      { label: "Activiteit", href: "/admin/logs", icon: ActivityIcon, match: "/admin/logs", resource: "audit" },
      { label: "Audit", href: "/admin/audit", icon: ShieldCheckIcon, match: "/admin/audit", resource: "audit" },
    ],
  },
  {
    // Instellingen (Team + algemene configuratie, gated on Configuratie read) and the personal
    // Kluis (every staff member has their own, no capability gate).
    label: "Systeem",
    items: [
      { label: "Instellingen", href: "/admin/instellingen", icon: SettingsIcon, match: "/admin/instellingen", resource: "configuratie", kind: "instellingen" },
      { label: "Kluis", href: "/admin/kluis", icon: VaultIcon, match: "/admin/kluis" },
    ],
  },
];

export function AdminSidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [avatarError, setAvatarError] = useState(false);
  const isActive = (match: string) => location.pathname.startsWith(match);
  const isAdmin = user?.roles.includes("admin");
  const caps = user?.capabilities;

  const canResource = (resource: string) => isAdmin || (!!caps && levelRank(caps[resource as keyof typeof caps] ?? "none") >= 1);
  // Datasets this user may view; the Dataconsole group hides the rest (server gates per-dataset too).
  const accessibleDatasets = CONSOLE_DATASETS.filter((d) => canResource(d.resource));

  const itemVisible = (item: NavItem) => {
    if (item.kind === "dataconsole") return accessibleDatasets.length > 0;
    if (item.adminOnly) return !!isAdmin;
    if (!item.resource || !caps) return true;
    return isAdmin || levelRank(caps[item.resource as keyof typeof caps] ?? "none") >= 1;
  };

  const consoleOpen = location.pathname.startsWith("/admin/scholen");
  const currentSlug = currentDatasetSlug(location.pathname);

  const visibleGroups = NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter(itemVisible) })).filter(
    (g) => g.items.length > 0,
  );

  const name = user ? user.preferredName || [user.givenName, user.familyName].filter(Boolean).join(" ") : "";
  const initials =
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? "")
      .join("") || "?";

  const avatarUrl = user?.personId ? `/api/avatars/${user.personId}` : null;

  return (
    <aside className="flex h-full w-full flex-col border-r border-border bg-background">
      {/* Brand */}
      <div className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-4">
        <img src="/favicon.png" alt="" className="size-5 shrink-0" />
        <span className="truncate text-sm font-semibold tracking-tight text-foreground">Mijn KUVO Woerden</span>
      </div>

      {/* Section links — each group under a heading, separated by a hairline divider. */}
      <nav className="flex-1 space-y-0.5 overflow-y-auto p-3" aria-label="Hoofdnavigatie">
        {visibleGroups.map((group, gi) => (
          <div key={group.label ?? `group-${gi}`} className={gi > 0 ? "mt-2 border-t border-border/70 pt-3" : undefined}>
            {group.label && (
              <p className="px-3 pb-1.5 text-[11px] font-semibold tracking-wide text-muted-foreground/60">{group.label}</p>
            )}
            {group.items.map((item) => {
              // Expandable Dataconsole group: parent link opens the first dataset; while in the
              // section the datasets render as sub-items underneath, and it collapses on leaving.
              if (item.kind === "dataconsole") {
                return (
                  <div key="dataconsole">
                    <Link
                      to={item.href}
                      onClick={onNavigate}
                      className={`flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                        consoleOpen ? "text-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                      }`}
                    >
                      <item.icon className="size-4 shrink-0" />
                      <span className="flex-1">{item.label}</span>
                      <ChevronRightIcon className={`size-3.5 shrink-0 text-muted-foreground/70 transition-transform ${consoleOpen ? "rotate-90" : ""}`} />
                    </Link>
                    {consoleOpen && (
                      <div className="mt-0.5 ml-4 space-y-0.5 border-l border-border/70 pl-2">
                        {accessibleDatasets.map((d) => {
                          const active = currentSlug === (d.slug ?? d.key);
                          return (
                            <Link
                              key={d.key}
                              to={datasetHref(d)}
                              onClick={onNavigate}
                              className={`block rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors ${
                                active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                              }`}
                            >
                              {d.shortLabel ?? d.label}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              if (item.kind === "instellingen") {
                const instellingenOpen = location.pathname.startsWith("/admin/instellingen");
                const currentSlugInst = location.pathname.split("/admin/instellingen/")[1]?.split("/")[0] ?? "";
                const visibleInst = INSTELLINGEN_ITEMS.filter((i) => !i.adminOnly || isAdmin);
                return (
                  <div key="instellingen">
                    <Link
                      to={`/admin/instellingen/${visibleInst[0]?.slug ?? "configuratie"}`}
                      onClick={onNavigate}
                      className={`flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                        instellingenOpen ? "text-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                      }`}
                    >
                      <item.icon className="size-4 shrink-0" />
                      <span className="flex-1">{item.label}</span>
                      <ChevronRightIcon className={`size-3.5 shrink-0 text-muted-foreground/70 transition-transform ${instellingenOpen ? "rotate-90" : ""}`} />
                    </Link>
                    {instellingenOpen && (
                      <div className="mt-0.5 ml-4 space-y-0.5 border-l border-border/70 pl-2">
                        {visibleInst.map((i) => {
                          const activeInst = currentSlugInst === i.slug;
                          return (
                            <Link
                              key={i.slug}
                              to={`/admin/instellingen/${i.slug}`}
                              onClick={onNavigate}
                              className={`block rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors ${
                                activeInst ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                              }`}
                            >
                              {i.label}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              const active = isActive(item.match);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={onNavigate}
                  className={`flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    active
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  }`}
                >
                  <item.icon className="size-4 shrink-0" />
                  {item.label}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Profile + logout: one compact row (avatar + name link, logout icon on the right) */}
      <div className="flex shrink-0 items-center gap-1 border-t border-border p-2">
        <Link
          to="/admin/profiel"
          onClick={onNavigate}
          className="flex min-w-0 flex-1 items-center gap-2.5 rounded-md px-2 py-1.5 transition-colors hover:bg-accent"
        >
          {avatarUrl && !avatarError ? (
            <img src={avatarUrl} alt="" className="size-8 shrink-0 rounded-full object-cover" onError={() => setAvatarError(true)} />
          ) : (
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted">
              <UserIcon className="size-4 text-muted-foreground" />
            </span>
          )}
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{name || "Beheerder"}</span>
        </Link>
        <button
          type="button"
          onClick={logout}
          aria-label="Uitloggen"
          title="Uitloggen"
          className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <LogOutIcon className="size-4" />
        </button>
      </div>
    </aside>
  );
}
